import { useListTenants } from "@fermata/grpc-zustand/tenant"
import type { Tenant } from "@proto/gen/users/v1/service"
import type React from "react"
import { createContext, useContext, useEffect, useMemo, useState } from "react"
import { createListTenantsRequest } from "../lib/grpc-list-requests"
import { getTenantSlug } from "../lib/tenant-utils"
import { useAuth } from "./AuthContext"

interface TenantContextType {
  tenant: Tenant | null
  tenantSlug: string
  isLoading: boolean
  error: Error | null
}

const TenantContext = createContext<TenantContextType | undefined>(undefined)

interface TenantProviderProps {
  children: React.ReactNode
}

export function TenantProvider({ children }: TenantProviderProps) {
  const { user } = useAuth()
  const [tenantSlug, setTenantSlug] = useState<string>(() => getTenantSlug())
  const [error, setError] = useState<Error | null>(null)

  // Listen for URL changes (for SPA navigation)
  useEffect(() => {
    const handleUrlChange = () => {
      const newSlug = getTenantSlug()
      if (newSlug !== tenantSlug) {
        setTenantSlug(newSlug)
      }
    }

    window.addEventListener("popstate", handleUrlChange)
    return () => window.removeEventListener("popstate", handleUrlChange)
  }, [tenantSlug])

  // Fetch tenant by slug
  const tenantsRequest = useMemo(
    () =>
      createListTenantsRequest({
        slug: tenantSlug,
        pagination: { offset: 0, limit: 1 },
      }),
    [tenantSlug]
  )

  const hasToken =
    typeof window !== "undefined" ? Boolean(localStorage.getItem("auth_token")) : false
  const enabled = Boolean(user) || hasToken

  const { data, loading } = useListTenants(tenantsRequest, enabled)

  // Extract tenant from response
  const tenant = useMemo(() => {
    if (!data?.tenants || data.tenants.length === 0) {
      return null
    }
    return data.tenants[0]
  }, [data])

  // Handle tenant not found
  useEffect(() => {
    if (!loading && !tenant && tenantSlug) {
      setError(new Error(`Tenant not found: ${tenantSlug}`))
    } else {
      setError(null)
    }
  }, [loading, tenant, tenantSlug])

  const value: TenantContextType = {
    tenant,
    tenantSlug,
    isLoading: loading,
    error,
  }

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
}

export function useTenant() {
  const context = useContext(TenantContext)
  if (context === undefined) {
    throw new Error("useTenant must be used within a TenantProvider")
  }
  return context
}
